// Sequenced chunk uploader with retry, used by the browser recorder and the file uploader.
// Chunks are uploaded strictly in seq order so a lost chunk never leaves a gap behind it.

export type UploadFn = (seq: number, blob: Blob, signal: AbortSignal) => Promise<void>;

export interface UploaderStatus {
  uploaded: number;
  pending: number;
  failing: boolean; // currently retrying after at least one failure
  lastError?: string;
}

export interface ChunkUploaderOptions {
  upload: UploadFn;
  /** Base delay between retries in ms (doubles up to maxDelayMs). */
  baseDelayMs?: number;
  maxDelayMs?: number;
  /** Give up after this many consecutive failures on one chunk (default: never). */
  maxAttempts?: number;
  onStatus?: (s: UploaderStatus) => void;
  sleep?: (ms: number, signal: AbortSignal) => Promise<void>;
}

const defaultSleep = (ms: number, signal: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('aborted', 'AbortError'));
      return;
    }
    const t = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = (): void => {
      clearTimeout(t);
      reject(new DOMException('aborted', 'AbortError'));
    };
    signal.addEventListener('abort', onAbort, { once: true });
  });

export class ChunkUploader {
  private readonly queue: Array<{ seq: number; blob: Blob }> = [];
  private readonly abort = new AbortController();
  private uploadedCount = 0;
  private running = false;
  private done: Promise<void> = Promise.resolve();
  private failing = false;
  private lastError: string | undefined;
  private fatal: Error | null = null;

  constructor(private readonly opts: ChunkUploaderOptions) {}

  get status(): UploaderStatus {
    const s: UploaderStatus = { uploaded: this.uploadedCount, pending: this.queue.length, failing: this.failing };
    if (this.lastError) s.lastError = this.lastError;
    return s;
  }

  enqueue(seq: number, blob: Blob): void {
    if (this.fatal) throw this.fatal;
    this.queue.push({ seq, blob });
    this.emit();
    if (!this.running) this.done = this.run();
  }

  /** Resolves when every enqueued chunk is uploaded; rejects on abort or when maxAttempts is exhausted. */
  async drain(): Promise<void> {
    await this.done;
    if (this.fatal) throw this.fatal;
  }

  cancel(): void {
    this.abort.abort();
  }

  private emit(): void {
    this.opts.onStatus?.(this.status);
  }

  private async run(): Promise<void> {
    this.running = true;
    const sleep = this.opts.sleep ?? defaultSleep;
    const base = this.opts.baseDelayMs ?? 1000;
    const max = this.opts.maxDelayMs ?? 30_000;
    try {
      while (this.queue.length > 0) {
        const item = this.queue[0]!;
        let attempt = 0;
        for (;;) {
          if (this.abort.signal.aborted) throw new DOMException('aborted', 'AbortError');
          try {
            await this.opts.upload(item.seq, item.blob, this.abort.signal);
            this.failing = false;
            this.lastError = undefined;
            break;
          } catch (err) {
            if (this.abort.signal.aborted) throw err;
            attempt += 1;
            this.failing = true;
            this.lastError = err instanceof Error ? err.message : String(err);
            this.emit();
            if (this.opts.maxAttempts !== undefined && attempt >= this.opts.maxAttempts) {
              throw new Error(`chunk ${item.seq} failed after ${attempt} attempts: ${this.lastError}`);
            }
            await sleep(Math.min(max, base * 2 ** (attempt - 1)), this.abort.signal);
          }
        }
        this.queue.shift();
        this.uploadedCount += 1;
        this.emit();
      }
    } catch (err) {
      this.fatal = err instanceof Error ? err : new Error(String(err));
      this.queue.length = 0;
      this.emit();
    } finally {
      this.running = false;
    }
  }
}

/** Pick a MediaRecorder mime type the browser supports (Chrome/Firefox: webm+opus; Safari: mp4). */
export function pickRecorderMime(): string | undefined {
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];
  if (typeof MediaRecorder === 'undefined') return undefined;
  return candidates.find((c) => MediaRecorder.isTypeSupported(c));
}

export function extensionForMime(mime: string): string {
  if (mime.includes('webm')) return 'webm';
  if (mime.includes('mp4')) return 'mp4';
  if (mime.includes('ogg')) return 'ogg';
  return 'bin';
}

/** Split a file into fixed-size byte slices (the worker concatenates chunks byte-wise). */
export function sliceFile(file: Blob, sliceBytes = 8 * 1024 * 1024): Blob[] {
  const out: Blob[] = [];
  for (let off = 0; off < file.size; off += sliceBytes) out.push(file.slice(off, Math.min(file.size, off + sliceBytes)));
  if (out.length === 0) out.push(file);
  return out;
}
